// src/pages/approval/ApprovalInProgress.jsx
import React, { useEffect, useState, useCallback } from "react";
import api from "../../api";
import styles from "./ApprovalInProgress.module.css";
import ExpenseResolutionPreview, { buildExpenseResolutionProps } from "../../components/preview/expense/ExpenseResolutionPreview";
import IncomeResolutionPreview, { buildIncomeResolutionProps } from "../../components/preview/income/IncomeResolutionPreview";
import { RESOLUTION_DOCUMENT_TYPES } from "../../utils/resolutionDraft";

const DOC_TYPE_LABEL = {
  income_draft: "수입기안",
  expense_draft: "지출기안",
  income_resolution: "수입결의",
  expense_resolution: "지출결의",
  leave_request: "휴가신청서",
};

const STATUS_LABEL = { draft: "작성중", pending: "결재중", approved: "승인", rejected: "반려" };
const STATUS_COLOR = { draft: "#888", pending: "#e67e22", approved: "#27ae60", rejected: "#e74c3c" };
// skipped(생략) = 결재 단계를 승인 없이 종결한 것(예: 퇴임). 회색 — 승인(초록)과 섞이지 않게.
const RESULT_LABEL = { pending: "대기", approved: "승인", rejected: "반려", skipped: "생략" };
const RESULT_COLOR = { pending: "#aaa", approved: "#27ae60", rejected: "#e74c3c", skipped: "#7f8c8d" };

export default function ApprovalInProgress() {
  const [submitters, setSubmitters] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/draft/documents/by-submitter/");
      setSubmitters(Array.isArray(data) ? data : []);
      if (data.length > 0) {
        setSelectedUser(data[0]);
        if (data[0].documents.length > 0) {
          setSelectedDoc(data[0].documents[0]);
        }
      }
    } catch (err) {
      console.error("❌ 데이터 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSelectedDoc(user.documents[0] || null);
  };

  // ✅ 지출결의/수입결의 — WEHAGO 미리보기를 그리려면 by-submitter가 주는 요약
  // 데이터(template_data 없음)로는 부족해서 선택 시 상세를 한 번 더 조회한다.
  const [resolutionDetail, setResolutionDetail] = useState(null);
  useEffect(() => {
    if (!selectedDoc || !RESOLUTION_DOCUMENT_TYPES.includes(selectedDoc.document_type)) {
      setResolutionDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/draft/documents/${selectedDoc.document_id}/`);
        if (!cancelled) setResolutionDetail(data);
      } catch (err) {
        console.error("❌ 지출결의 상세 조회 실패:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDoc]);

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>불러오는 중...</div>;

  return (
    <div className={styles.wrap}>

      {/* 왼쪽: 담당자 목록 */}
      <div className={styles.userPane}>
        <div className={styles.paneHeader}>담당자</div>
        {submitters.length === 0 ? (
          <div className={styles.empty}>진행 중인 결재가 없습니다.</div>
        ) : submitters.map((user) => {
          const pendingCount = user.documents.filter(d => d.status === 'pending').length;
          return (
            <div
              key={user.user_id}
              className={`${styles.userItem} ${selectedUser?.user_id === user.user_id ? styles.activeUser : ""}`}
              onClick={() => handleSelectUser(user)}
            >
              <div className={styles.userName}>{user.user_name}</div>
              <div className={styles.userMeta}>
                <span className={styles.position}>{user.position}</span>
                {pendingCount > 0 && (
                  <span className={styles.pendingBadge}>{pendingCount}건 진행중</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 가운데: 문서 목록 */}
      <div className={styles.docPane}>

        <div className={styles.paneHeader}>
          <span>{selectedUser ? `${selectedUser.user_name}님의 문서` : "문서 목록"}</span>
          <button className={styles.refreshBtn} onClick={fetchData}>🔄</button>
        </div>


        {!selectedUser || selectedUser.documents.length === 0 ? (
          <div className={styles.empty}>문서가 없습니다.</div>
        ) : selectedUser.documents.map((doc) => (
          <div
            key={doc.document_id}
            className={`${styles.docItem} ${selectedDoc?.document_id === doc.document_id ? styles.activeDoc : ""}`}
            onClick={() => setSelectedDoc(doc)}
          >
            <div className={styles.docTop}>
              <span className={styles.docType}>{DOC_TYPE_LABEL[doc.document_type]}</span>
              <span className={styles.statusBadge} style={{ color: STATUS_COLOR[doc.status] }}>
                {STATUS_LABEL[doc.status]}
              </span>
            </div>
            <div className={styles.docTitle}>{doc.title}</div>
            <div className={styles.docMeta}>
              {doc.partner_name} · {Number(doc.amount).toLocaleString()}원
            </div>
            <div className={styles.docDate}>{doc.draft_date}</div>
          </div>
        ))}
      </div>

      {/* 오른쪽: 결재선 현황 */}
      <div className={styles.detailPane}>
        <div className={styles.paneHeader}>결재선 현황</div>
        {!selectedDoc ? (
          <div className={styles.empty}>문서를 선택하세요.</div>
        ) : (
          <>
            {/* 문서 정보 — 지출결의/수입결의는 WEHAGO 양식으로, 그 외는 기존 요약 카드 그대로 */}
            {selectedDoc.document_type === "expense_resolution" ? (
              resolutionDetail ? (
                <ExpenseResolutionPreview {...buildExpenseResolutionProps(resolutionDetail)} />
              ) : (
                <div className={styles.docInfoCard}>불러오는 중...</div>
              )
            ) : selectedDoc.document_type === "income_resolution" ? (
              resolutionDetail ? (
                <IncomeResolutionPreview {...buildIncomeResolutionProps(resolutionDetail)} />
              ) : (
                <div className={styles.docInfoCard}>불러오는 중...</div>
              )
            ) : (
              <div className={styles.docInfoCard}>
                <div className={styles.docInfoTitle}>{selectedDoc.title}</div>
                <div className={styles.docInfoMeta}>
                  <span>{DOC_TYPE_LABEL[selectedDoc.document_type]}</span>
                  <span>·</span>
                  <span>{selectedDoc.category}</span>
                  <span>·</span>
                  <span>{selectedDoc.partner_name}</span>
                  <span>·</span>
                  <span style={{ fontWeight: 700 }}>{Number(selectedDoc.amount).toLocaleString()}원</span>
                </div>
                <div className={styles.draftNo}>{selectedDoc.draft_no}</div>
              </div>
            )}

            <div className={styles.approvalFlow}>
              {(selectedDoc.all_approval_lines || []).map((line, i) => (
                <React.Fragment key={line.id}>
                  <div
                    className={styles.approvalStep}
                    style={{
                      borderColor: line.result === 'approved' ? '#27ae60'
                        : line.result === 'rejected' ? '#e74c3c'
                        : line.result === 'skipped' ? '#9aa5ad' : '#d0d7de'
                    }}
                  >
                    <div className={styles.approvalType}>{line.approval_type}</div>
                    <div className={styles.approvalName}>{line.approver__name}</div>
                    <div className={styles.approvalPosition}>{line.position}</div>
                    <div
                      className={styles.approvalResult}
                      style={{ color: RESULT_COLOR[line.result] }}
                    >
                      {RESULT_LABEL[line.result]}
                    </div>
                    {line.approved_at && (
                      <div className={styles.approvalDate}>
                        {line.approved_at.slice(0, 10)}
                      </div>
                    )}
                    {line.comment && (
                      <div className={styles.approvalComment}>💬 {line.comment}</div>
                    )}
                  </div>
                  {i < (selectedDoc.all_approval_lines?.length || 0) - 1 && (
                    <div className={styles.approvalArrow}>→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}